import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

/**
 * Translations. The Spanish text in the code is the key (gettext style): Spanish needs no
 * resources and the components stay readable. English lives in `src/locales/en/*.json`, one file
 * per area, merged here. Plurals are written out in the code
 * (`n === 1 ? t('1 tarea') : t('{{count}} tareas', { count: n })`).
 */
export const languages = { es: 'Español', en: 'English' } as const;
export type Language = keyof typeof languages;

const STORAGE_KEY = 'nexo.lang';

const english = Object.assign(
  {},
  ...Object.values(
    import.meta.glob<Record<string, string>>('../locales/en/*.json', {
      eager: true,
      import: 'default',
    }),
  ),
) as Record<string, string>;

const isLanguage = (value: unknown): value is Language =>
  typeof value === 'string' && value in languages;

/** Saved choice, else the browser's language (anything that isn't Spanish gets English). */
function initialLanguage(): Language {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (isLanguage(saved)) return saved;
  } catch {
    // Storage unavailable: fall through to the browser language.
  }
  const browser = typeof navigator === 'undefined' ? 'es' : navigator.language.slice(0, 2);
  return browser === 'es' ? 'es' : 'en';
}

void i18n.use(initReactI18next).init({
  lng: initialLanguage(),
  fallbackLng: 'es',
  supportedLngs: Object.keys(languages),
  resources: { en: { translation: english } },
  // Natural-language keys contain ":" and ".".
  keySeparator: false,
  nsSeparator: false,
  interpolation: { escapeValue: false },
  returnEmptyString: false,
});

const listeners = new Set<(language: Language) => void>();

function applyLanguage(language: Language) {
  document.documentElement.lang = language;
  for (const listener of listeners) listener(language);
}
applyLanguage(currentLanguage());

i18n.on('languageChanged', (lng) => {
  if (isLanguage(lng)) applyLanguage(lng);
});

export function currentLanguage(): Language {
  return isLanguage(i18n.language) ? i18n.language : 'es';
}

/** Changes and remembers the interface language. */
export function setLanguage(language: Language): void {
  try {
    localStorage.setItem(STORAGE_KEY, language);
  } catch {
    // Not persisted (private mode): still switches for this session.
  }
  void i18n.changeLanguage(language);
}

/** Runs `listener` now and whenever the language changes; returns the unsubscribe. */
export function onLanguage(listener: (language: Language) => void): () => void {
  listeners.add(listener);
  listener(currentLanguage());
  return () => listeners.delete(listener);
}

/** BCP 47 locale for Intl formatting. */
export const intlLocale = () => (currentLanguage() === 'en' ? 'en-GB' : 'es-ES');

/** Translates outside React (stores, helpers, toasts). Same keys as `useTranslation().t`. */
export const t = i18n.t.bind(i18n);

export default i18n;
